import { IMap } from "./MultiMap";

export interface IBag<K> extends BagExtra<K>, IMap<K, number> {}

export interface ForEach<K> {
  forEach(callback: (amount: number, keys: K) => void);
}

/**
 * Eine Bag ist eine Collection, welche die Anzahl der Elemente zählt, also eine Map Key => Anzahl.
 * Anzahl ist reel (erlaubt negative und Nachkomma). Typischer Anwendungszweck ist das Zählen
 * von Produkten in einer Szene.
 */
export class Bag<K> implements IBag<K> {
  rootMap: Map<K, number> = new Map();

  constructor(rootMap?: Map<K, number>) {
    this.rootMap = (rootMap ?? new Map<K, number>()) as any;
  }

  inc(key: K, amount: number): void {
    const oldAmount = this.get(key);
    const newAmount = oldAmount + amount;
    this.rootMap.set(key, newAmount);
  }

  dec(keys: K, amount: number): void {
    this.inc(keys, -amount);
  }

  from(iterable: BagExtra<K> | Map<K, number> | Set<K> | Array<K>): Bag<K> {
    this.clear();
    bagForAllGeneric<K>(iterable, (key, amount) => this.inc(key, amount));
    return this;
  }

  incAll(otherBag: ForEach<K>): void {
    otherBag.forEach((amount, keys) => this.inc(keys, amount));
  }

  decAll(otherBag: ForEach<K>): void {
    otherBag.forEach((amount, keys) => this.dec(keys, amount));
  }

  multiply(value: number) {
    this.rootMap.forEach((number, key) => this.rootMap.set(key, number * value));
  }

  add(value: number) {
    this.rootMap.forEach((number, key) => this.rootMap.set(key, number + value));
  }

  set(key: K, value: number): this {
    this.rootMap.set(key, value);
    return this;
  }

  get(key: K): number {
    return this.rootMap.has(key) ? (this.rootMap.get(key) as number) ?? Number.NaN : 0;
  }

  delete(key: K): boolean {
    return this.rootMap.delete(key);
  }

  has(key: any): boolean {
    return this.rootMap.has(key);
  }

  clear(): void {
    this.rootMap.clear();
  }

  get size(): number {
    return this.rootMap.size;
  }

  get length(): number {
    return this.size;
  }

  *entries(): IterableIterator<[K, number]> {
    const entries: [K, number][] = [];
    this.forEach((value, key) => entries.push([key, value]));
    yield* entries;
  }

  *keys(): IterableIterator<K> {
    const entries: K[] = [];
    this.forEach((value, key) => entries.push(key));
    yield* entries;
  }

  *values(): IterableIterator<number> {
    const entries: number[] = [];
    this.forEach((value, key) => entries.push(value));
    yield* entries;
  }

  [Symbol.iterator](): IterableIterator<[K, number]> {
    return this.entries();
  }

  get [Symbol.toStringTag](): string {
    return "[object Bag]";
  }

  map<R>(mapper: (value: number, key: K) => R): R[] {
    const list: R[] = [];
    this.forEach((value, key) => list.push(mapper(value, key)));
    return list;
  }

  forEach(callback: (value: number, key: K, map: IMap<K, number>) => void): void {
    this.rootMap.forEach((value, key, bag) => callback(value, key, this));
  }

  toArray(): [K, number][] {
    return Array.from(this.entries());
  }

  toObject(): Record<string, number> {
    const o = {} as any;
    this.forEach((v, k) => (o[k] = v));
    return o;
  }

  getMaxKey(): K {
    return _.maxBy(this.toArray(), ([key, amount]) => amount)?.[0];
  }

  getMinKey(): K {
    return _.minBy(this.toArray(), ([key, amount]) => amount)?.[0];
  }
}

export interface BagExtra<K> {
  inc(keys: K, amount: number): void;
  dec(keys: K, amount: number): void;
  multiply(value: number): void;
  add(value: number): void;
}
export function bagForAllGeneric<K>(iterable: any, callback: (key: K, amount: number) => void) {
  if (iterable == null) {
    return;
  }
  if (iterable instanceof Set || Array.isArray(iterable)) {
    iterable.forEach((key: K) => callback(key, 1));
  } else {
    iterable.forEach((value: number, key: K) => callback(key, value));
  }
}
